import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { talentAPI, bookmarkAPI, userHideAPI, talentSearchPreferenceAPI } from '../../services/api';
import Header from '../Header';
import Footer from '../Footer';
import BottomNavigationBar from '../BottomNavigationBar';
import TalentDetailPanel from './TalentDetailPanel';
import TalentFilters from './TalentFilters';
// Import JobsPage CSS to use the same styling
import '../Jobs/JobsPage.css';

const TalentSearchPage = ({ 
  onHomeClick, 
  onCategoriesClick, 
  onHowItWorksClick, 
  onProfileClick, 
  onAccountSettingsClick, 
  onForumClick,
  onJobsClick,
  onLoginClick, 
  onSignupClick 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [talents, setTalents] = useState([]);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreTalents, setHasMoreTalents] = useState(true);
  const [totalTalents, setTotalTalents] = useState(0);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [hiddenUserIds, setHiddenUserIds] = useState(new Set()); // Set of user IDs user has hidden/reported
  const [filters, setFilters] = useState({
    searchQuery: '',
    location: '',
    workType: '',
    skills: '',
    bookmarkListId: '',
    preferenceId: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    searchQuery: '',
    location: '',
    workType: '',
    skills: '',
    bookmarkListId: '',
    preferenceId: '',
  });
  const [bookmarkLists, setBookmarkLists] = useState([]);
  const [savedPreferences, setSavedPreferences] = useState([]);
  const [showSavePreferenceModal, setShowSavePreferenceModal] = useState(false);
  const [preferenceTitle, setPreferenceTitle] = useState('');
  const [loadingPreferences, setLoadingPreferences] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch bookmark lists on mount if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchBookmarkLists();
      fetchSavedPreferences();
    }
  }, [isAuthenticated]);

  const fetchBookmarkLists = async () => {
    try {
      // Only fetch lists that have talents (from talent_bookmarks table)
      const response = await bookmarkAPI.getLists('talents');
      console.log('Bookmark lists response:', response);
      if (response.success) {
        const lists = response.data || [];
        console.log('Setting bookmark lists:', lists);
        setBookmarkLists(lists);
      } else {
        console.error('Failed to fetch bookmark lists:', response);
      }
    } catch (err) {
      console.error('Error fetching bookmark lists:', err);
      setBookmarkLists([]);
    }
  };

  const fetchSavedPreferences = async () => {
    try {
      setLoadingPreferences(true);
      const response = await talentSearchPreferenceAPI.getAll();
      if (response.success) {
        setSavedPreferences(response.data || []);
      } else {
        console.error('Failed to fetch saved preferences:', response);
      }
    } catch (err) {
      console.error('Error fetching saved preferences:', err);
      setSavedPreferences([]);
    } finally {
      setLoadingPreferences(false);
    }
  };

  // Initialize search query from URL params
  useEffect(() => {
    const searchParam = searchParams.get('search');
    if (searchParam) {
      setFilters(prev => ({ ...prev, searchQuery: searchParam }));
      setAppliedFilters(prev => ({ ...prev, searchQuery: searchParam }));
    }
  }, []); // Only run once on mount

  // Watch for URL search param changes and update filters, then trigger search
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const searchParam = searchParams.get('search') || '';
    
    // Update filters with new search query
    setFilters(prev => {
      if (prev.searchQuery !== searchParam) {
        return { ...prev, searchQuery: searchParam };
      }
      return prev;
    });
    
    setAppliedFilters(prev => {
      if (prev.searchQuery !== searchParam) {
        // Reset pagination when search changes
        setCurrentPage(1);
        setTalents([]);
        return { ...prev, searchQuery: searchParam };
      }
      return prev;
    });
  }, [searchParams, isAuthenticated]); // Watch for URL changes

  // Initial load - only if authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    setCurrentPage(1);
    setTalents([]);
    fetchTalents(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Fetch talents when applied filters change - only if authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    // Reset pagination when filters change
    setCurrentPage(1);
    setTalents([]);
    fetchTalents(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, isAuthenticated]);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle body scroll when modal is open
  useEffect(() => {
    if (showMobileModal || showFilters) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showMobileModal, showFilters]);

  const fetchTalents = async (page = 1, reset = false) => {
    // Don't fetch if not authenticated
    if (!isAuthenticated) {
      setLoading(false);
      setLoadingMore(false);
      return;
    }
    
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      // Build API parameters from applied filters
      const apiParams = {
        per_page: 10,
        page: page
      };
      
      // Add search query from applied filters or URL (applied filters take priority)
      const searchQuery = appliedFilters.searchQuery || searchParams.get('search') || '';
      if (searchQuery && searchQuery.trim() !== '') {
        apiParams.search = searchQuery.trim();
      }

      // Add filter parameters
      if (appliedFilters.location && appliedFilters.location.trim() !== '') {
        apiParams.location = appliedFilters.location.trim();
      }

      if (appliedFilters.workType && appliedFilters.workType !== '') {
        apiParams.work_type = appliedFilters.workType;
      }

      if (appliedFilters.skills && appliedFilters.skills.trim() !== '') {
        apiParams.skills = appliedFilters.skills.trim();
      }

      // Use bookmark API if bookmark filter is active, otherwise use regular talent search
      let response;
      if (appliedFilters.bookmarkListId && appliedFilters.bookmarkListId !== '') {
        // Fetch bookmarked talents
        apiParams.list_id = appliedFilters.bookmarkListId;
        response = await bookmarkAPI.getBookmarkedTalents(apiParams);
      } else {
        // Fetch all talents
        response = await talentAPI.search(apiParams);
      }
      
      if (response.success) {
        // Handle paginated response
        let talentsData = [];
        let paginationInfo = null;
        
        if (response.data?.data && Array.isArray(response.data.data)) {
          talentsData = response.data.data;
          paginationInfo = response.data;
        } else if (response.pagination) {
          talentsData = response.data || [];
          paginationInfo = response.pagination;
        } else if (Array.isArray(response.data)) {
          talentsData = response.data;
        }
        
        // Append or replace talents based on reset flag
        if (reset) {
          setTalents(talentsData);
        } else {
          setTalents(prev => [...prev, ...talentsData]);
        }
        
        // Update pagination state
        if (paginationInfo) {
          const totalPages = paginationInfo.last_page || Math.ceil((paginationInfo.total || 0) / 10);
          setHasMoreTalents(page < totalPages);
          setTotalTalents(paginationInfo.total || talentsData.length);
        } else {
          // If no pagination info, assume no more talents if we got less than requested
          setHasMoreTalents(talentsData.length === 10);
        }
        
        setCurrentPage(page);
        
        // Auto-select first talent if reset and we have talents
        if (reset && talentsData.length > 0 && !selectedTalent) {
          setSelectedTalent(talentsData[0]);
        } else if (reset && talentsData.length === 0) {
          setSelectedTalent(null);
        }
      }
    } catch (err) {
      console.error('Error fetching talents:', err);
      if (reset) {
        setTalents([]);
        setSelectedTalent(null);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMoreTalents) {
      const nextPage = currentPage + 1;
      fetchTalents(nextPage, false);
    }
  };

  const handleTalentSelect = (talent, e) => {
    // Check if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
    const isModifierPressed = e?.ctrlKey || e?.metaKey;
    
    if (isModifierPressed && talent?.username) {
      // Open public profile in new tab
      e?.preventDefault();
      e?.stopPropagation();
      window.open(`/talent/${talent.username}`, '_blank', 'noopener,noreferrer');
      return;
    }
    
    // Normal selection - show in detail panel
    setSelectedTalent(talent);
    // Show modal on mobile, otherwise show in side panel
    if (isMobile) {
      setShowMobileModal(true);
    }
  };

  // Handle user report success - mark as hidden (no reload)
  const handleUserReportSuccess = (userId, shouldHide = true) => {
    // If hideUser is checked, mark user as hidden immediately (overlay will show on the user item)
    if (shouldHide) {
      setHiddenUserIds(prev => new Set([...prev, userId]));
      // Don't clear selection - user might want to view the user details
      // Don't reload talents - keep the list as is with overlay showing
    }
    // Modal will close automatically via ReportUserModal's setTimeout
    // No need to refresh the talents list
  };

  // Handle unhide user - remove from hidden list
  const handleUnhideUser = async (userId) => {
    try {
      const response = await userHideAPI.unhide(userId);
      if (response.success) {
        // Remove from hiddenUserIds to hide the overlay
        setHiddenUserIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(userId);
          return newSet;
        });
      } else {
        console.error('Failed to unhide user:', response.message);
      }
    } catch (error) {
      console.error('Error unhiding user:', error);
    }
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value === '' ? '' : value
    }));
    // Don't fetch talents automatically - wait for apply button click
  };

  const handleBookmarkChange = (bookmarkListId) => {
    // Auto-apply bookmark filter changes immediately for better UX
    setFilters(prev => ({
      ...prev,
      bookmarkListId: bookmarkListId || ''
    }));
    setAppliedFilters(prev => ({
      ...prev,
      bookmarkListId: bookmarkListId || ''
    }));
    // Reset pagination when bookmark filter changes
    setCurrentPage(1);
    setTalents([]);
  };

  const handleApplyFilters = () => {
    // Apply the current filters and trigger search
    setAppliedFilters({ ...filters });
    
    // Update URL with search query if it changed
    const searchQuery = filters.searchQuery?.trim() || '';
    if (searchQuery) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('search', searchQuery);
        return newParams;
      });
    } else {
      // Remove search param if empty
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('search');
        return newParams;
      });
    }
    
    // Close mobile modal after applying
    if (isMobile) {
      setShowFilters(false);
    }
  };

  const handleSavePreference = () => {
    // Check if there are any active filters
    const hasFilters = filters.searchQuery?.trim() || 
                       filters.location?.trim() || 
                       filters.workType || 
                       filters.skills?.trim();
    
    if (!hasFilters) {
      alert('Please apply at least one filter before saving as preference.');
      return;
    }
    
    // Show modal to get preference title
    setShowSavePreferenceModal(true);
    setPreferenceTitle('');
  };

  const handleSavePreferenceConfirm = async () => {
    if (!preferenceTitle.trim()) {
      alert('Please enter a title for your search preference.');
      return;
    }

    try {
      const response = await talentSearchPreferenceAPI.create({
        title: preferenceTitle.trim(),
        search_query: filters.searchQuery?.trim() || null,
        location: filters.location?.trim() || null,
        work_type: filters.workType || null,
        skills: filters.skills?.trim() || null,
        is_active: true
      });

      if (response.success) {
        setShowSavePreferenceModal(false);
        setPreferenceTitle('');
        await fetchSavedPreferences();
        alert('Search preference saved successfully! You will be notified when new talents match this search.');
      } else {
        alert(response.message || 'Failed to save preference. Please try again.');
      }
    } catch (err) {
      console.error('Error saving preference:', err);
      alert('Failed to save preference. Please try again.');
    }
  };

  const handlePreferenceChange = (preferenceId) => {
    if (!preferenceId || preferenceId === '') {
      // Clear preference filters but keep bookmark filter
      const newFilters = {
        searchQuery: '',
        location: '',
        workType: '',
        skills: '',
        bookmarkListId: filters.bookmarkListId || '',
        preferenceId: '',
      };
      setFilters(newFilters);
      setAppliedFilters(newFilters);
      
      // Clear search param from URL
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('search');
        return newParams;
      });
      return;
    }

    // Find the selected preference
    const preference = savedPreferences.find(p => p.id.toString() === preferenceId.toString());
    if (!preference) return;

    // Apply the preference filters
    const newFilters = {
      searchQuery: preference.search_query || '',
      location: preference.location || '',
      workType: preference.work_type || '',
      skills: preference.skills || '',
      bookmarkListId: filters.bookmarkListId || '', // Keep bookmark filter if active
      preferenceId: preferenceId,
    };
    
    setFilters(newFilters);
    setAppliedFilters(newFilters);
    
    // Update URL with search query if it exists
    if (newFilters.searchQuery) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('search', newFilters.searchQuery);
        return newParams;
      });
    } else {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('search');
        return newParams;
      });
    }
    
    // Close mobile modal if open
    if (isMobile) {
      setShowFilters(false);
    }
  };

  const handleApplyPreference = async (preference) => {
    // Apply the preference filters
    const newFilters = {
      searchQuery: preference.search_query || '',
      location: preference.location || '',
      workType: preference.work_type || '',
      skills: preference.skills || '',
      bookmarkListId: filters.bookmarkListId || '', // Keep bookmark filter if active
    };
    
    setFilters(newFilters);
    setAppliedFilters(newFilters);
    
    // Update URL with search query if it exists
    if (newFilters.searchQuery) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('search', newFilters.searchQuery);
        return newParams;
      });
    } else {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('search');
        return newParams;
      });
    }
    
    // Close mobile modal if open
    if (isMobile) {
      setShowFilters(false);
    }
  };

  const handleDeletePreference = async (preferenceId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this search preference?')) {
      return;
    }

    try {
      const response = await talentSearchPreferenceAPI.delete(preferenceId);
      if (response.success) {
        // If the deleted preference was currently selected, clear it
        if (filters.preferenceId && filters.preferenceId.toString() === preferenceId.toString()) {
          handlePreferenceChange('');
        }
        await fetchSavedPreferences();
      } else {
        alert(response.message || 'Failed to delete preference. Please try again.');
      }
    } catch (err) {
      console.error('Error deleting preference:', err);
      alert('Failed to delete preference. Please try again.');
    }
  };

  const handleTogglePreferenceActive = async (preferenceId, e) => {
    e.stopPropagation();
    try {
      const response = await talentSearchPreferenceAPI.toggleActive(preferenceId);
      if (response.success) {
        await fetchSavedPreferences();
      } else {
        alert(response.message || 'Failed to update preference. Please try again.');
      }
    } catch (err) {
      console.error('Error toggling preference:', err);
      alert('Failed to update preference. Please try again.');
    }
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      searchQuery: '',
      location: '',
      workType: '',
      skills: '',
      bookmarkListId: filters.bookmarkListId || '', // Keep bookmark filter
      preferenceId: '', // Clear preference selection
    };
    setFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
    // Clear search param from URL
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('search');
      return newParams;
    });
    // Talents will be fetched automatically via useEffect when appliedFilters change
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  // Use provided handlers or create defaults with navigate
  const handleHomeClick = onHomeClick || (() => navigate('/'));
  const handleCategoriesClick = onCategoriesClick || (() => navigate('/categories'));
  const handleHowItWorksClick = onHowItWorksClick || (() => navigate('/#how-it-works'));
  const handleProfileClick = onProfileClick || (() => navigate('/profile'));
  const handleAccountSettingsClick = onAccountSettingsClick || (() => navigate('/settings'));
  const handleForumClick = onForumClick || (() => navigate('/forum'));
  const handleJobsClick = onJobsClick || (() => navigate('/vacancies'));

  return (
    <div className="jobs-page jobs-with-bottom-nav" style={{ minHeight: '100vh' }}>
      {/* Header - visible on desktop/tablet, hidden on mobile */}
      <div className="jobs-header-nav">
        <Header
          onLoginClick={onLoginClick || (() => navigate('/login'))}
          onSignupClick={onSignupClick || (() => navigate('/signup'))}
          isLoggedIn={isAuthenticated}
          user={user}
          onHomeClick={handleHomeClick}
          onCategoriesClick={handleCategoriesClick}
          onHowItWorksClick={handleHowItWorksClick}
          onProfileClick={handleProfileClick}
          onAccountSettingsClick={handleAccountSettingsClick}
          onForumClick={handleForumClick}
          onJobsClick={handleJobsClick}
          currentPage="talent-search"
        />
      </div>
      
      <div className="jobs-container">
        {!isAuthenticated ? (
          <div className="jobs-empty" style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '60vh',
            padding: '60px 20px',
            textAlign: 'center'
          }}>
            <div className="jobs-empty-icon" style={{ marginBottom: '24px' }}>
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#cbd5e1' }}>
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
                <path d="M12 11v6"></path>
                <path d="M9 14h6"></path>
              </svg>
            </div>
            <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#1f2937', marginBottom: '12px' }}>
              Sign in to search for talent
            </h2>
            <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '32px', maxWidth: '500px' }}>
              Please sign in to access the talent search feature. This helps us protect our platform from bots and ensures a better experience for everyone.
            </p>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <button 
                onClick={onLoginClick || (() => navigate('/login'))}
                style={{
                  padding: '12px 24px',
                  background: '#7a5fb8',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#6b4fa0';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#7a5fb8';
                }}
              >
                Sign In
              </button>
              <button 
                onClick={onSignupClick || (() => navigate('/signup'))}
                style={{
                  padding: '12px 24px',
                  background: 'transparent',
                  color: '#7a5fb8',
                  border: '1px solid #7a5fb8',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#f5f3ff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                }}
              >
                Create Account
              </button>
            </div>
          </div>
        ) : (
          <>
        {/* Filters Section - Above Content */}
        <div className="jobs-filters-section">
          <div className="jobs-filters-header">
            <h2>Hunt a Talent</h2>
            <div className="jobs-filters-header-right">
              {!isMobile && Object.values(appliedFilters).some(v => {
                return v !== '' && v !== 'all';
              }) && (
                <button 
                  className="jobs-filter-clear-all-btn"
                  onClick={handleClearFilters}
                >
                  Clear all
                </button>
              )}
              {/* Mobile Filter Icon Button */}
              {isMobile && (
                <button 
                  className="jobs-filter-icon-btn"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="4" y1="21" x2="4" y2="14"></line>
                    <line x1="4" y1="10" x2="4" y2="3"></line>
                    <line x1="12" y1="21" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12" y2="3"></line>
                    <line x1="20" y1="21" x2="20" y2="16"></line>
                    <line x1="20" y1="12" x2="20" y2="3"></line>
                    <line x1="1" y1="14" x2="7" y2="14"></line>
                    <line x1="9" y1="8" x2="15" y2="8"></line>
                    <line x1="17" y1="16" x2="23" y2="16"></line>
                  </svg>
                  {Object.values(appliedFilters).some(v => {
                    return v !== '' && v !== 'all';
                  }) && <span className="jobs-filter-badge"></span>}
                </button>
              )}
            </div>
          </div>
          
          {/* Saved Search Preferences and Bookmark Dropdowns - Right after heading */}
          {isAuthenticated && ((savedPreferences && savedPreferences.length > 0) || (bookmarkLists && bookmarkLists.length > 0)) && (
            <div style={{ 
              padding: isMobile ? '0 16px 16px 16px' : '0 24px 16px 24px',
              borderBottom: '1px solid #e5e7eb',
              marginBottom: '16px'
            }}>
              <div style={{ 
                display: 'flex', 
                gap: '16px',
                flexWrap: isMobile ? 'wrap' : 'nowrap',
                alignItems: 'flex-start'
              }}>
                {/* Saved Search Preferences Dropdown */}
                {savedPreferences && savedPreferences.length > 0 && (
                  <div style={{ flex: '1', minWidth: isMobile ? '100%' : '200px' }}>
                    <label className="job-filter-section-header" style={{ marginBottom: '8px' }}>
                      <span>Saved Search Preference</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={filters.preferenceId || ''}
                        onChange={(e) => {
                          handlePreferenceChange(e.target.value);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 36px 10px 12px',
                          fontSize: '14px',
                          fontWeight: filters.preferenceId ? 600 : 500,
                          color: filters.preferenceId ? '#7a5fb8' : '#1f2937',
                          border: `1px solid ${filters.preferenceId ? '#7a5fb8' : '#e5e7eb'}`,
                          borderRadius: '6px',
                          background: filters.preferenceId ? 'linear-gradient(to bottom, #faf5ff, #f5f3ff)' : 'white',
                          cursor: 'pointer',
                          appearance: 'none',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <option value="">Select a saved preference...</option>
                        {savedPreferences.map(pref => (
                          <option key={pref.id} value={pref.id}>
                            {pref.title} {!pref.is_active && '(Inactive)'}
                          </option>
                        ))}
                      </select>
                      <div style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none'
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#6b7280' }}>
                          <polyline points="6,9 12,15 18,9"></polyline>
                        </svg>
                      </div>
                      {filters.preferenceId && filters.preferenceId !== '' && (
                        <button
                          onClick={() => handlePreferenceChange('')}
                          style={{
                            position: 'absolute',
                            right: '36px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            color: '#7a5fb8',
                            fontSize: '12px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            padding: '4px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#f3f4f6';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'none';
                          }}
                          title="Clear preference filter"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      )}
                    </div>
                    {filters.preferenceId && filters.preferenceId !== '' && (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px 12px',
                        background: '#f5f3ff',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
                        </svg>
                        <span>Filters applied from saved preference</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Bookmark List Dropdown */}
                {bookmarkLists && bookmarkLists.length > 0 && (
                  <div style={{ flex: '1', minWidth: isMobile ? '100%' : '200px' }}>
                    <label className="job-filter-section-header" style={{ marginBottom: '8px' }}>
                      <span>Bookmark List</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={filters.bookmarkListId || ''}
                        onChange={(e) => {
                          handleBookmarkChange(e.target.value);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 36px 10px 12px',
                          fontSize: '14px',
                          fontWeight: filters.bookmarkListId ? 600 : 500,
                          color: filters.bookmarkListId ? '#7a5fb8' : '#1f2937',
                          border: `1px solid ${filters.bookmarkListId ? '#7a5fb8' : '#e5e7eb'}`,
                          borderRadius: '6px',
                          background: filters.bookmarkListId ? 'linear-gradient(to bottom, #faf5ff, #f5f3ff)' : 'white',
                          cursor: 'pointer',
                          appearance: 'none',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <option value="">Global Search (All Talents)</option>
                        {bookmarkLists.map(list => (
                          <option key={list.id} value={list.id}>
                            {list.title || 'Untitled List'} {list.talents_count > 0 && `(${list.talents_count})`}
                          </option>
                        ))}
                      </select>
                      <div style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none'
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#6b7280' }}>
                          <polyline points="6,9 12,15 18,9"></polyline>
                        </svg>
                      </div>
                      {filters.bookmarkListId && filters.bookmarkListId !== '' && (
                        <button
                          onClick={() => handleBookmarkChange('')}
                          style={{
                            position: 'absolute',
                            right: '36px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            color: '#7a5fb8',
                            fontSize: '12px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            padding: '4px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#f3f4f6';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'none';
                          }}
                          title="Clear bookmark filter"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      )}
                    </div>
                    {filters.bookmarkListId && filters.bookmarkListId !== '' && (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px 12px',
                        background: '#f5f3ff',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
                        </svg>
                        <span>Filters will only apply to talents in this list</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Desktop Filters - Always visible */}
          {!isMobile && (
            <div className="jobs-filters-content">
              <TalentFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                onApplyFilters={handleApplyFilters}
                onSavePreference={handleSavePreference}
                talentCount={talents.length}
                isHorizontal={true}
              />
            </div>
          )}
        </div>

        {/* Mobile Filter Modal/Popup */}
        {isMobile && showFilters && createPortal(
          <div className="jobs-filter-mobile-modal-overlay" onClick={() => setShowFilters(false)}>
            <div className="jobs-filter-mobile-modal" onClick={(e) => e.stopPropagation()}>
              <div className="jobs-filter-mobile-modal-header">
                <h3>Filters</h3>
                <div className="jobs-filter-mobile-modal-header-right">
                  {Object.values(filters).some(v => {
                    return v !== '' && v !== 'all';
                  }) && (
                    <button 
                      className="jobs-filter-mobile-clear-btn"
                      onClick={handleClearFilters}
                    >
                      Clear all
                    </button>
                  )}
                  <button 
                    className="jobs-filter-mobile-close-btn"
                    onClick={() => setShowFilters(false)}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>
              <div className="jobs-filter-mobile-modal-content" style={{ display: 'block', visibility: 'visible', minHeight: '300px', padding: '20px' }}>
                {/* Saved Search Preferences Dropdown for Mobile */}
                {isAuthenticated && savedPreferences && savedPreferences.length > 0 && (
                  <div style={{ 
                    marginBottom: '20px',
                    paddingBottom: '20px',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <label className="job-filter-section-header" style={{ marginBottom: '8px' }}>
                      <span>Saved Search Preference</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={filters.preferenceId || ''}
                        onChange={(e) => {
                          handlePreferenceChange(e.target.value);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 36px 10px 12px',
                          fontSize: '14px',
                          fontWeight: filters.preferenceId ? 600 : 500,
                          color: filters.preferenceId ? '#7a5fb8' : '#1f2937',
                          border: `1px solid ${filters.preferenceId ? '#7a5fb8' : '#e5e7eb'}`,
                          borderRadius: '6px',
                          background: filters.preferenceId ? 'linear-gradient(to bottom, #faf5ff, #f5f3ff)' : 'white',
                          cursor: 'pointer',
                          appearance: 'none'
                        }}
                      >
                        <option value="">Select a saved preference...</option>
                        {savedPreferences.map(pref => (
                          <option key={pref.id} value={pref.id}>
                            {pref.title} {!pref.is_active && '(Inactive)'}
                          </option>
                        ))}
                      </select>
                      <div style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none'
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#6b7280' }}>
                          <polyline points="6,9 12,15 18,9"></polyline>
                        </svg>
                      </div>
                    </div>
                    {filters.preferenceId && filters.preferenceId !== '' && (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px 12px',
                        background: '#f5f3ff',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
                        </svg>
                        <span>Filters applied from saved preference</span>
                      </div>
                    )}
                  </div>
                )}
                {/* Bookmark List Dropdown for Mobile */}
                {isAuthenticated && bookmarkLists && bookmarkLists.length > 0 && (
                  <div style={{ 
                    marginBottom: '20px',
                    paddingBottom: '20px',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <label className="job-filter-section-header" style={{ marginBottom: '8px' }}>
                      <span>Bookmark List</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={filters.bookmarkListId || ''}
                        onChange={(e) => {
                          handleBookmarkChange(e.target.value);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 36px 10px 12px',
                          fontSize: '14px',
                          fontWeight: filters.bookmarkListId ? 600 : 500,
                          color: filters.bookmarkListId ? '#7a5fb8' : '#1f2937',
                          border: `1px solid ${filters.bookmarkListId ? '#7a5fb8' : '#e5e7eb'}`,
                          borderRadius: '6px',
                          background: filters.bookmarkListId ? 'linear-gradient(to bottom, #faf5ff, #f5f3ff)' : 'white',
                          cursor: 'pointer',
                          appearance: 'none'
                        }}
                      >
                        <option value="">Global Search (All Talents)</option>
                        {bookmarkLists.map(list => (
                          <option key={list.id} value={list.id}>
                            {list.title || 'Untitled List'} {list.talents_count > 0 && `(${list.talents_count})`}
                          </option>
                        ))}
                      </select>
                      <div style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none'
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#6b7280' }}>
                          <polyline points="6,9 12,15 18,9"></polyline>
                        </svg>
                      </div>
                    </div>
                    {filters.bookmarkListId && filters.bookmarkListId !== '' && (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px 12px',
                        background: '#f5f3ff',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
                        </svg>
                        <span>Filters will only apply to talents in this list</span>
                      </div>
                    )}
                  </div>
                )}
                {filters && (
                  <TalentFilters
                    filters={filters}
                    onFilterChange={(key, value) => {
                      handleFilterChange(key, value);
                    }}
                    onClearFilters={handleClearFilters}
                    onApplyFilters={handleApplyFilters}
                    onSavePreference={handleSavePreference}
                    talentCount={talents.length}
                    isHorizontal={false}
                    hideHeader={true}
                  />
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

        <div className="jobs-layout">
          {/* Left Sidebar - Talent List */}
          <div className="jobs-sidebar">
            {loading ? (
              <div className="jobs-loading">
                <div className="jobs-spinner"></div>
                <span>Loading talents...</span>
              </div>
            ) : talents.length === 0 ? (
              <div className="jobs-empty">
                <div className="jobs-empty-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </div>
                <h3>No talents found</h3>
                <p>Try adjusting your search or filters</p>
              </div>
            ) : (
              <div className="jobs-list">
                {talents.map((talent) => {
                  // Split name into first and last name
                  const nameParts = talent.name ? talent.name.trim().split(/\s+/) : [];
                  const firstName = nameParts[0] || '';
                  const lastName = nameParts.slice(1).join(' ') || '';
                  const isHidden = hiddenUserIds.has(talent.id);
                  
                  return (
                    <div
                      key={talent.id}
                      className={`job-item ${selectedTalent?.id === talent.id ? 'active' : ''} ${isHidden ? 'reported-hidden' : ''}`}
                      onClick={(e) => handleTalentSelect(talent, e)}
                      style={{ padding: '14px', cursor: 'pointer', overflow: 'hidden', position: 'relative' }}
                    >
                      {isHidden && (
                        <div className="job-item-overlay">
                          <div className="job-item-overlay-content">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                              <polyline points="22 4 12 14.01 9 11.01"></polyline>
                            </svg>
                            <span>Reported and hidden for you</span>
                            <button 
                              className="job-item-overlay-undo-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUnhideUser(talent.id);
                              }}
                              title="Undo - Show this user again"
                            >
                              Undo
                            </button>
                          </div>
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', width: '100%', minWidth: 0 }}>
                        {/* Profile Picture */}
                        <div style={{ width: '52px', height: '52px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0, border: '2px solid #e5e7eb', background: 'white' }}>
                          {talent.profile_photo_url ? (
                            <img src={talent.profile_photo_url} alt={talent.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #7a5fb8 0%, #6b4fa0 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '20px', fontWeight: 700 }}>
                              {firstName?.charAt(0)?.toUpperCase() || 'T'}
                            </div>
                          )}
                        </div>
                        
                        {/* Content - Compact Layout */}
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px', overflow: 'hidden' }}>
                          {/* Name and Job Seeking Status Row */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <h3 className="job-item-title" style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#1f2937', lineHeight: '1.3', flex: '0 1 auto', minWidth: 0 }}>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                {firstName}
                                {lastName && <span style={{ fontWeight: 500, color: '#4b5563' }}> {lastName}</span>}
                              </span>
                            </h3>
                            
                            {/* Job Seeking Status - After Name */}
                            {talent.job_seeking_status && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                                {talent.job_seeking_status === 'actively-seeking' ? (
                                  <>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#059669' }}>
                                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                                      <path d="M8 12l2 2 4-4"></path>
                                    </svg>
                                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#059669', whiteSpace: 'nowrap' }}>
                                      Actively Seeking
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#9ca3af' }}>
                                      <path d="M9 12l2 2 4-4"></path>
                                      <path d="M21 12c.552 0 1-.448 1-1V5c0-.552-.448-1-1-1H3c-.552 0-1 .448-1 1v6c0 .552.448 1 1 1h18z"></path>
                                      <path d="M3 12v6c0 .552.448 1 1 1h16c.552 0 1-.448 1-1v-6"></path>
                                    </svg>
                                    <span style={{ fontSize: '12px', fontWeight: 500, color: '#6b7280', whiteSpace: 'nowrap' }}>
                                      {talent.job_seeking_status.charAt(0).toUpperCase() + talent.job_seeking_status.slice(1).replace(/-/g, ' ')}
                                    </span>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {/* Professional Title - Plain Text */}
                          {talent.professional_title && (
                            <div style={{ fontSize: '13px', fontWeight: 500, color: '#7a5fb8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {talent.professional_title}
                            </div>
                          )}
                          
                          {/* Work Type and Timezone - Inline */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', width: '100%', flexWrap: 'wrap' }}>
                            {/* Work Type */}
                            {talent.work_type && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#374151', minWidth: 0 }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#9ca3af', flexShrink: 0 }}>
                                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                                  <line x1="8" y1="21" x2="16" y2="21"></line>
                                  <line x1="12" y1="17" x2="12" y2="21"></line>
                                </svg>
                                <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {talent.work_type.charAt(0).toUpperCase() + talent.work_type.slice(1).replace('-', ' ')}
                                </span>
                              </div>
                            )}
                            
                            {/* Timezone Code - Right after Work Type */}
                            {talent.timezone_display && (() => {
                              // Extract timezone code (e.g., "EST", "PST", "UTC") from timezone_display
                              // Common formats: "America/New_York (EST)", "EST", "UTC+5", etc.
                              let timezoneCode = talent.timezone_display;
                              
                              // Try to extract code from parentheses: "America/New_York (EST)" -> "EST"
                              const parenMatch = talent.timezone_display.match(/\(([A-Z]{2,5})\)/);
                              if (parenMatch) {
                                timezoneCode = parenMatch[1];
                              } else {
                                // Try to extract from formats like "UTC+5" or "EST-5"
                                const utcMatch = talent.timezone_display.match(/([A-Z]{2,5})/);
                                if (utcMatch && utcMatch[1].length <= 5) {
                                  timezoneCode = utcMatch[1];
                                } else {
                                  // If it's a timezone name like "America/New_York", try to get abbreviation
                                  // For now, just take first 3-4 uppercase letters or common abbreviations
                                  const abbrevMatch = talent.timezone_display.match(/\b([A-Z]{2,4})\b/);
                                  if (abbrevMatch) {
                                    timezoneCode = abbrevMatch[1];
                                  }
                                }
                              }
                              
                              return (
                                <>
                                  {talent.work_type && (
                                    <span style={{ color: '#d1d5db', fontSize: '12px' }}>•</span>
                                  )}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#374151', minWidth: 0 }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#9ca3af', flexShrink: 0 }}>
                                      <circle cx="12" cy="12" r="10"></circle>
                                      <polyline points="12,6 12,12 16,14"></polyline>
                                    </svg>
                                    <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {timezoneCode}
                                    </span>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Load More Button */}
            {!loading && talents.length > 0 && hasMoreTalents && (
              <div className="jobs-load-more-container">
                <button 
                  className="jobs-load-more-btn"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <div className="jobs-load-more-spinner"></div>
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <span>Load More</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <polyline points="19,12 12,19 5,12"></polyline>
                      </svg>
                    </>
                  )}
                </button>
                {totalTalents > 0 && (
                  <p className="jobs-load-more-info">
                    Showing {talents.length} of {totalTalents} talents
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Right Panel - Talent Details */}
          <div className="jobs-detail-panel">
            {selectedTalent ? (
              <div className="jobs-detail-panel-wrapper" style={{ position: 'relative' }}>
                {hiddenUserIds.has(selectedTalent.id) && (
                  <div className="job-detail-overlay">
                    <div className="job-detail-overlay-content">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                      <span>Reported and hidden for you</span>
                      <button 
                        className="job-detail-overlay-undo-btn"
                        onClick={() => handleUnhideUser(selectedTalent.id)}
                        title="Undo - Show this user again"
                      >
                        Undo
                      </button>
                    </div>
                  </div>
                )}
                <TalentDetailPanel 
                  talent={selectedTalent}
                  currentUser={user}
                  isAuthenticated={isAuthenticated}
                  onUserReportSuccess={handleUserReportSuccess}
                  isHidden={selectedTalent ? hiddenUserIds.has(selectedTalent.id) : false}
                  onUnhideUser={handleUnhideUser}
                />
              </div>
            ) : (
              <div className="jobs-no-selection">
                <div className="no-selection-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </div>
                <h3>Select a talent to view profile</h3>
                <p>Choose a talent from the list to see their details</p>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Talent Detail Modal */}
        {showMobileModal && selectedTalent && isMobile && createPortal(
          <div className="job-detail-mobile-modal-overlay" onClick={() => setShowMobileModal(false)}>
            <div className="job-detail-mobile-modal" onClick={(e) => e.stopPropagation()}>
              <div className="job-detail-mobile-modal-header">
                <h2>Talent Profile</h2>
                <button 
                  className="job-detail-mobile-modal-close"
                  onClick={() => setShowMobileModal(false)}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <div className="job-detail-mobile-modal-content" style={{ position: 'relative' }}>
                {selectedTalent && hiddenUserIds.has(selectedTalent.id) && (
                  <div className="job-detail-overlay">
                    <div className="job-detail-overlay-content">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                      <span>Reported and hidden for you</span>
                      <button 
                        className="job-detail-overlay-undo-btn"
                        onClick={() => handleUnhideUser(selectedTalent.id)}
                        title="Undo - Show this user again"
                      >
                        Undo
                      </button>
                    </div>
                  </div>
                )}
                <TalentDetailPanel 
                  talent={selectedTalent}
                  currentUser={user}
                  isAuthenticated={isAuthenticated}
                  onUserReportSuccess={handleUserReportSuccess}
                  isHidden={selectedTalent ? hiddenUserIds.has(selectedTalent.id) : false}
                  onUnhideUser={handleUnhideUser}
                />
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Save Preference Modal */}
        {showSavePreferenceModal && createPortal(
          <div 
            className="jobs-filter-mobile-modal-overlay" 
            onClick={() => {
              setShowSavePreferenceModal(false);
              setPreferenceTitle('');
            }}
            style={{ 
              zIndex: 10000,
              alignItems: isMobile ? 'flex-end' : 'center',
              padding: isMobile ? '0' : '20px'
            }}
          >
            <div 
              className="jobs-filter-mobile-modal" 
              onClick={(e) => e.stopPropagation()}
              style={{ 
                maxWidth: isMobile ? '100%' : '500px', 
                width: isMobile ? '100%' : '90%',
                borderRadius: isMobile ? '20px 20px 0 0' : '16px',
                maxHeight: isMobile ? '85vh' : '90vh',
                minHeight: isMobile ? '400px' : 'auto',
                animation: isMobile ? 'slideUpModal 0.3s ease forwards' : 'modalSlideIn 0.3s ease-out',
                boxShadow: isMobile ? '0 -4px 20px rgba(0, 0, 0, 0.15)' : '0 20px 60px rgba(0, 0, 0, 0.3)'
              }}
            >
              <div className="jobs-filter-mobile-modal-header">
                <h3>Save Search Preference</h3>
                <button 
                  className="jobs-filter-mobile-close-btn"
                  onClick={() => {
                    setShowSavePreferenceModal(false);
                    setPreferenceTitle('');
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <div className="jobs-filter-mobile-modal-content" style={{ padding: '20px' }}>
                <p style={{ marginBottom: '16px', color: '#6b7280', fontSize: '14px' }}>
                  Give your search preference a name so you can easily find and apply it later. You'll also receive notifications when new talents match these filters.
                </p>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontSize: '14px', 
                    fontWeight: 600, 
                    color: '#374151' 
                  }}>
                    Preference Title *
                  </label>
                  <input
                    type="text"
                    value={preferenceTitle}
                    onChange={(e) => setPreferenceTitle(e.target.value)}
                    placeholder="e.g., Remote React Developers"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#7a5fb8'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    autoFocus
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSavePreferenceConfirm();
                      }
                    }}
                  />
                </div>
                <div style={{ 
                  display: 'flex', 
                  gap: '12px', 
                  justifyContent: 'flex-end' 
                }}>
                  <button
                    onClick={() => {
                      setShowSavePreferenceModal(false);
                      setPreferenceTitle('');
                    }}
                    style={{
                      padding: '10px 20px',
                      background: 'transparent',
                      color: '#6b7280',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#f3f4f6';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'transparent';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSavePreferenceConfirm}
                    disabled={!preferenceTitle.trim()}
                    style={{
                      padding: '10px 20px',
                      background: preferenceTitle.trim() ? '#7a5fb8' : '#d1d5db',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: preferenceTitle.trim() ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (preferenceTitle.trim()) {
                        e.target.style.background = '#6b4fa0';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (preferenceTitle.trim()) {
                        e.target.style.background = '#7a5fb8';
                      }
                    }}
                  >
                    Save Preference
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
          </>
        )}
      </div>

      <Footer />
      <BottomNavigationBar
        onHomeClick={handleHomeClick}
        onCategoriesClick={handleCategoriesClick}
        onHowItWorksClick={handleHowItWorksClick}
        onProfileClick={handleProfileClick}
        onAccountSettingsClick={handleAccountSettingsClick}
        onForumClick={handleForumClick}
        onJobsClick={handleJobsClick}
        onLoginClick={onLoginClick || (() => navigate('/login'))}
        onSignupClick={onSignupClick || (() => navigate('/signup'))}
        isLoggedIn={isAuthenticated}
        user={user}
      />
    </div>
  );
};

export default TalentSearchPage;
